import React, { useState, useEffect, useCallback } from 'react'
import {
  useCreate,
  useGetIdentity,
  useModal,
  useOne,
  useSelect,
} from '@refinedev/core'
import { Drawer, Button, Form, Card, Modal, FormInstance, Tabs } from 'antd'
import { QuestionCircleOutlined, LoadingOutlined } from '@ant-design/icons'
import { CRM_UPDATE_QUIZ, LEAD_URL, VENUE_URL } from 'urls'
import { InfoProps, OptionProps, Question, QuestionProps } from './Question'
import { ILeadQuizData } from 'interfaces/crm'
import {
  budgetSplitPercentage,
  CateringOptions,
  daysData,
  events,
  GuestCount,
  guestCounts,
  HotelCost,
  HotelOptions,
  meals,
  MealTypeKeys,
  PaxData,
  PaxInformation,
  TotalBudget,
  venueData,
  venueDecided,
  venueLocationSegments,
} from '../questionInterface'
import { VenueCost, CateringCost } from '../consts'
import { IProductShowcase } from 'interfaces/product'
import { IStaff } from 'interfaces/staff'
import { LeadServices } from './ServicesModal'
import { NotesInput } from './NotesInput'
import LeadFlow from './QuizEngine/LeadQuizFlowOld'
import LeadQuizFlow from './QuizEngine/LeadQuizFlow'

const { confirm } = Modal

const PreSaleQuiz = ({ lead }: { lead: any }) => {
  const { show, close, visible } = useModal()

  return (
    <div>
      <Button
        size="small"
        type="primary"
        ghost
        icon={<QuestionCircleOutlined />}
        onClick={() => show()}>
        Update Quiz
      </Button>
      {visible && <QuizDrawer close={close} visible={visible} lead={lead} />}
    </div>
  )
}

const QuizDrawer = ({
  close,
  visible,
  lead,
}: {
  close: any
  visible: boolean
  lead: any
}) => {
  const [form] = Form.useForm()
  const [questions, setQuestions] = useState<QuestionProps[]>([])

  useEffect(() => {
    if (visible) {
      setQuestions([])
    }
  }, [visible])

  const {
    data: quizData,
    status: quizStatus,
  } = useOne<ILeadQuizData>({
    resource: LEAD_URL,
    id: `${lead}/get_quiz_data`,
  });

  const handleClose = useCallback(() => {
    if (form.isFieldsTouched()) {
      confirm({
        title: 'Discard Changes?',
        content: 'Are you sure you want to discard your changes?',
        okText: 'Discard',
        okType: 'danger',
        cancelText: 'Continue Editing',
        onOk() {
          resetFormAndClose()
        },
      })
    } else {
      resetFormAndClose()
    }
  }, [close])

  const resetFormAndClose = () => {
    setQuestions([])
    form.resetFields()
    close()
  }

  const renderQuizContent = (isCustomerData: boolean, key: string) => {
    if (quizStatus === 'loading') {
      return <LoadingOutlined style={{ fontSize: 24 }} spin />
    }

    if (quizStatus === 'success') {
      const data = isCustomerData
        ? quizData?.data?.customer_json_data
        : quizData?.data?.json_data

      if (!data || data.length === 0 || (data.length > 0 && data[0].index !== null)) {
        return (
          <LeadQuizFlow
            questions={questions}
            setQuestions={setQuestions}
            form={form}
            close={close}
            lead={lead}
            isCustomerData={isCustomerData}
            key={key}
          />
        )
      } else if (data.length > 0 && data[0].index === null) {
        return (
          <LeadFlow
            questions={questions}
            setQuestions={setQuestions}
            form={form}
            close={close}
            lead={lead}
            isCustomerData={isCustomerData}
            key={key}
          />
        )
      }
    }

    return null
  }

  return (
    <Drawer
      id={lead}
      title="Quiz Drawer"
      placement="right"
      onClose={handleClose}
      open={visible}
      destroyOnClose
      closable={false}
      width={700}
      extra={<Button onClick={handleClose}>Close</Button>}>
      {visible && (
        <Tabs
          defaultActiveKey="1"
          destroyInactiveTabPane={true}
          onChange={() => setQuestions([])}>
          <Tabs.TabPane tab="Quiz Form" key="1" id="lead-quiz-1">
            {renderQuizContent(false, "1")}
          </Tabs.TabPane>
          <Tabs.TabPane tab="Customer Quiz Form" key="2" id="lead-quiz-2">
            {renderQuizContent(true, "2")}
          </Tabs.TabPane>
        </Tabs>
      )}
    </Drawer>
  )
}

export default PreSaleQuiz
